import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { employeesApi, type Employee } from "@/lib/employees-api";
import { cn } from "@/lib/utils";

/** How many matches a search shows; narrow the search to find anyone else. */
const RESULT_LIMIT = 20;
const SEARCH_DELAY_MS = 250;

const optionLabel = (e: Pick<Employee, "name" | "empCode">) => `${e.name} · ${e.empCode}`;

export interface EmployeePickerProps {
  /** Accessible name of the trigger, e.g. "Reporting manager". */
  label: string;
  /** Selected employee id; null/undefined = none. */
  value: string | null | undefined;
  /** Text for `value` when the caller already has it (e.g. the row's `managerName`); otherwise it's looked up. */
  selectedLabel?: string | null;
  onChange: (id: string | null, label: string | null) => void;
  /** The employee being edited — never offered as their own manager. */
  excludeId?: string;
  noneLabel?: string;
  /** Set by `<Field>` so its label names this control. */
  id?: string;
  "aria-describedby"?: string;
}

/**
 * Pick one active employee by searching the API.
 *
 * Replaces a `<Select>` fed by the page's loaded rows: with one page of 200 out
 * of 1,824 staff, an employee whose manager wasn't on that page saw an empty
 * box, as if no manager were set (2026-10-29). The search runs on the server,
 * so anyone in the tenant can be found, and the current choice is shown by
 * name whatever is loaded.
 */
export function EmployeePicker({
  label,
  value,
  selectedLabel,
  onChange,
  excludeId,
  noneLabel = "— No manager —",
  id,
  "aria-describedby": describedBy,
}: EmployeePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedLabel, setFetchedLabel] = useState<string | null>(null);

  // Name the current choice when the caller couldn't.
  useEffect(() => {
    setFetchedLabel(null);
    if (!value || selectedLabel) return;
    let cancelled = false;
    employeesApi
      .get(value)
      .then((e) => { if (!cancelled) setFetchedLabel(optionLabel(e)); })
      .catch(() => { /* non-fatal — the trigger shows a neutral placeholder */ });
    return () => { cancelled = true; };
  }, [value, selectedLabel]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      employeesApi
        .list({ search: query.trim() || undefined, status: "ACTIVE", pageSize: RESULT_LIMIT })
        .then((r) => { if (!cancelled) setResults(r.data); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, query ? SEARCH_DELAY_MS : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, query]);

  const pick = (nextId: string | null, nextLabel: string | null) => {
    onChange(nextId, nextLabel);
    setOpen(false);
  };

  const shown = results.filter((e) => e.id !== excludeId);
  const display = value ? selectedLabel ?? fetchedLabel ?? "Selected employee" : noneLabel;

  return (
    // Modal, so the list scrolls and takes focus inside an open Dialog.
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          aria-describedby={describedBy}
          className="w-full justify-between font-normal"
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>{display}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by name, email or code…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandGroup>
              <CommandItem value="__none__" onSelect={() => pick(null, null)}>
                <Check className={cn("mr-2 h-4 w-4", value ? "opacity-0" : "opacity-100")} />
                {noneLabel}
              </CommandItem>
              {shown.map((e) => (
                <CommandItem key={e.id} value={e.id} onSelect={() => pick(e.id, optionLabel(e))}>
                  <Check className={cn("mr-2 h-4 w-4", e.id === value ? "opacity-100" : "opacity-0")} />
                  {optionLabel(e)}
                </CommandItem>
              ))}
            </CommandGroup>
            {loading ? (
              <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin opacity-60" /></div>
            ) : shown.length === 0 ? (
              <div className="py-3 text-center text-sm text-muted-foreground">
                {query.trim() ? `No active staff match "${query.trim()}".` : "No active staff."}
              </div>
            ) : shown.length >= RESULT_LIMIT ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">Showing the first {RESULT_LIMIT} — type to narrow.</div>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
